import { MMButton, MMLabelValue, RecurringPreviewRow, SendMoneyAmountRow, TrustedRecipientCheckbox } from '@components'
import { getAccountingCategoryLabel } from '@constants'
import { formatCurrencyForDisplay } from '@utils'
import styles from '@withdrawals/styles/WithdrawPreview.module.scss'
import {
  useCurrencies,
  useFeatureFlagState,
  useInternalTransferInitiateMutation,
  useInternalTransferPreviewMutation
} from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { formatFloat, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'

interface InternalTransferPreviewProps {
  selectedAccount?: AccountBalanceResponse
  formValues?: any
  email?: string
  onSubmit: () => void
  onCancel: () => void
}

const InternalTransferPreview = (props: InternalTransferPreviewProps) => {
  const { selectedAccount, formValues, email, onSubmit, onCancel } = props

  const accountingCategoryFF = useFeatureFlagState('WITHDRAW_ACCOUNTING_CATEGORY')

  const { getCurrencyPrecision } = useCurrencies()

  const [_, { data: transferPreview }] = useInternalTransferPreviewMutation({
    fixedCacheKey: 'internal-transfer-preview'
  })
  const [__, { error: transferError, isLoading: transferLoading }] = useInternalTransferInitiateMutation({
    fixedCacheKey: 'internal-transfer-initiate'
  })

  const { fee, feeCurrency, processingAmount, processingCurrency, walletAmount, walletCurrency, trustedRecipient } =
    transferPreview || {}

  // const { name: accountName } = selectedAccount || {}

  return (
    <div className={styles['withdraw-preview-wrapper']}>
      <div className='withdraw-preview-container amount-container'>
        <div className='main-amount-label'>{tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.AMOUNT', 'Amount')}</div>
        <div className='main-amount-value'>
          <span data-test='amount'>
            {formatFloat(walletAmount, getCurrencyPrecision(walletCurrency))}{' '}
            <span className='main-amount-currency'>{formatCurrencyForDisplay(walletCurrency)}</span>
          </span>
        </div>

        {fee > 0 && (
          <SendMoneyAmountRow
            label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.FEE', 'Fee')}
            value={
              <span>
                {formatFloat(fee, getCurrencyPrecision(feeCurrency))}{' '}
                <span className='row-amount-currency'>{formatCurrencyForDisplay(feeCurrency)}</span>
              </span>
            }
            data-test='fee-amount'
          />
        )}

        <SendMoneyAmountRow
          label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.NET_AMOUNT', 'Net amount')}
          value={
            <span>
              {formatFloat(processingAmount, getCurrencyPrecision(processingCurrency))}{' '}
              <span className='row-amount-currency'>{formatCurrencyForDisplay(processingCurrency)}</span>
            </span>
          }
          data-test='net-amount'
        />
      </div>

      <div className='withdraw-preview-container send-money-details'>
        <MMLabelValue
          variant='withdrawal'
          label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.FROM', 'From account')}
          value={selectedAccount?.name}
          data-test='account-name'
        />
        <MMLabelValue
          variant='withdrawal'
          label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.EMAIL', 'Recipient email')}
          value={email || formValues.getValue('recipientEmail')}
          data-test='recipient-email'
        />
        <MMLabelValue
          variant='withdrawal'
          label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.REFERENCE', 'Reference')}
          value={formValues.getValue('reference')}
          data-test='reference'
        />

        {accountingCategoryFF.isEnabled && (
          <MMLabelValue
            variant='withdrawal'
            label={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.CATEGORY', 'Payment category')}
            value={getAccountingCategoryLabel(formValues.getValue('accountingCategory'))}
            data-test='accounting-category'
          />
        )}

        <RecurringPreviewRow formValues={formValues} />
      </div>

      <TrustedRecipientCheckbox formValues={formValues} trustedRecipient={trustedRecipient} />

      <ErrorDisplay error={transferError} />

      <div className='preview-actions'>
        <MMButton
          type='button'
          size='large'
          variant='alternative'
          caption={tr('FRONTEND.WITHDRAW.INTERNAL_TRANSFER.PREVIEW.BUTTON_SEND', 'Send')}
          onClick={onSubmit}
          loading={transferLoading}
          disabled={transferLoading}
          data-test='button-send'
          fullWidth
        />
        <MMButton
          type='button'
          size='large'
          variant='tertiary'
          caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          onClick={onCancel}
          disabled={transferLoading}
          data-test='button-cancel'
          fullWidth
        />
      </div>
    </div>
  )
}

export default InternalTransferPreview
